import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useCreateFeedSource, usePreviewFeedSource } from 'src/api/queries';
import { PageHeader } from 'src/components/editorial/PageHeader';
import { Badge } from 'src/components/ui/Badge';
import { Button } from 'src/components/ui/Button';
import { Notice } from 'src/components/ui/Notice';
import { formatDateTime } from 'src/lib/time';
import type { FeedPreview, FeedPreviewWarning } from 'src/types/editorial';

/**
 * Subscribing to a site, in two steps: read the address, then decide.
 *
 * The roster's inline form used to be the whole flow — paste, press, find out afterwards. Most of
 * what goes wrong with a feed is invisible until something reads it: the path a newsroom published
 * for a decade now serves HTML, the publisher refuses any client without a browser's user agent,
 * the feed is real but carries no dates. All of that arrived as a row that quietly said "con
 * fallas", after the subscription existed.
 *
 * So this screen reads the address first and shows what it found — the publisher's own title, the
 * latest headlines, what is missing — and only then offers to subscribe. Verifying writes nothing,
 * so it can be repeated as often as an editor likes.
 */
export function NewFeedSourceWorkspace() {
  const navigate = useNavigate();
  const preview = usePreviewFeedSource();
  const create = useCreateFeedSource();

  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');

  const verified = preview.data?.preview;
  const readyToSubscribe = verified?.outcome === 'ok' && !verified.alreadySubscribed;

  /** A new address invalidates the last reading — including the name it suggested. */
  function changeUrl(nextUrl: string) {
    setUrl(nextUrl);
    if (preview.data || preview.error) {
      preview.reset();
      setName('');
    }
  }

  return (
    <div className="page-medium">
      <Link className="page-back" to="/suscripciones">
        ← Suscripciones
      </Link>

      <PageHeader title="Suscribir un sitio">
        Pega la liga del feed o de la portada. La revisamos antes de suscribir: si el sitio dejó de
        publicar RSS, si nos rechaza o si el feed no trae fechas, lo sabrás aquí y no cuando falle
        la primera lectura.
      </PageHeader>

      <form
        className="subscribe__step"
        onSubmit={event => {
          event.preventDefault();
          preview.mutate(url.trim(), {
            onSuccess: ({ preview: result }) => {
              if (result.outcome === 'ok') setName(result.suggestedName);
            },
          });
        }}
      >
        <label className="subscribe__field">
          <span>Liga del feed o de la portada</span>
          <input
            type="url"
            value={url}
            onChange={event => changeUrl(event.target.value)}
            required
            placeholder="https://www.jornada.com.mx/rss/edicion.xml"
            autoFocus
          />
        </label>

        <Button type="submit" variant="primary" busy={preview.isPending} disabled={url.trim().length === 0}>
          Revisar
        </Button>
      </form>

      {/* A refused address — privada, local, la de metadatos — llega como error, no como
          diagnóstico: no es algo que se ofrezca suscribir de otra forma. */}
      {preview.isError && (
        <Notice tone="contradicted" title="No se puede leer esa dirección" role="alert">
          <p>{preview.error instanceof Error ? preview.error.message : 'Error desconocido'}</p>
        </Notice>
      )}

      {verified && verified.outcome !== 'ok' && <PreviewFailure preview={verified} onUseGoogleNews={changeUrl} />}

      {verified?.outcome === 'ok' && (
        <section className="subscribe__found">
          <header className="subscribe__foundHead">
            <div>
              <p className="subscribe__eyebrow">Feed encontrado</p>
              <h2 className="section-title">{verified.title || verified.suggestedSourceId}</h2>
            </div>
            <Badge tone="supported">{verified.itemCount} publicaciones</Badge>
          </header>

          <dl className="subscribe__facts">
            <div>
              <dt>Feed</dt>
              <dd>
                <a className="mono" href={verified.feedUrl} target="_blank" rel="noreferrer noopener">
                  {verified.feedUrl}
                </a>
              </dd>
            </div>
            {verified.siteUrl && (
              <div>
                <dt>Sitio</dt>
                <dd>
                  <a className="mono" href={verified.siteUrl} target="_blank" rel="noreferrer noopener">
                    {verified.siteUrl}
                  </a>
                </dd>
              </div>
            )}
            <div>
              <dt>Se archivará como</dt>
              <dd className="mono">{verified.suggestedSourceId}</dd>
            </div>
          </dl>

          {/* Worth saying out loud: the editor pasted a page and we followed a link out of it, so
              the address being subscribed is not the one they typed. */}
          {verified.discoveredFromPage && (
            <Notice tone="unresolved" title="Encontramos el feed a partir de la página">
              <p>Pegaste una página; el feed que se va a leer es el de arriba.</p>
            </Notice>
          )}

          {verified.alreadySubscribed && (
            <Notice tone="unresolved" title="Ya está suscrito">
              <p>
                Este feed ya está en la lista como <strong>{verified.alreadySubscribed.name}</strong>
                {verified.alreadySubscribed.active ? ' y está activo.' : ', pausado. Actívalo en la lista en lugar de agregarlo otra vez.'}
              </p>
              <p>
                <Link to="/suscripciones">Ver la lista</Link>
              </p>
            </Notice>
          )}

          {verified.warnings.map(warning => (
            <Notice key={warning} tone="unresolved" title={WARNING_TITLE[warning]}>
              <p>{WARNING_DETAIL[warning]}</p>
            </Notice>
          ))}

          {verified.items.length > 0 && (
            <div className="subscribe__sample">
              <p className="subscribe__eyebrow">Lo último que publicó</p>
              <ul>
                {verified.items.map(item => (
                  <li key={item.link || item.title}>
                    <a href={item.link} target="_blank" rel="noreferrer noopener">
                      {item.title || 'Sin título'}
                    </a>
                    <span className="subscribe__sampleMeta mono">{item.publishedAt ? formatDateTime(item.publishedAt) : 'sin fecha'}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <form
            className="subscribe__step"
            onSubmit={event => {
              event.preventDefault();
              create.mutate(
                { url: verified.feedUrl, name: name.trim(), notes: notes.trim() },
                // Straight to what it brought in, filtered to this source: the roster answers
                // "which sites", and the editor just decided that. What they have not seen is the
                // posts.
                { onSuccess: ({ source }) => navigate(`/publicaciones?fuente=${encodeURIComponent(source.sourceId)}`) }
              );
            }}
          >
            <label className="subscribe__field">
              <span>Nombre</span>
              <input type="text" value={name} onChange={event => setName(event.target.value)} maxLength={120} placeholder={verified.suggestedName} />
            </label>

            <label className="subscribe__field">
              <span>Notas (opcional)</span>
              <textarea value={notes} onChange={event => setNotes(event.target.value)} maxLength={2000} rows={2} placeholder="Por qué se sigue este sitio" />
            </label>

            <Button type="submit" variant="primary" busy={create.isPending} disabled={!readyToSubscribe}>
              Suscribir y leer ahora
            </Button>

            {create.isError && (
              <Notice tone="contradicted" title="No se pudo suscribir" role="alert">
                <p>{create.error instanceof Error ? create.error.message : 'Error desconocido'}</p>
              </Notice>
            )}
          </form>
        </section>
      )}
    </div>
  );
}

/** The four diagnosable answers, each with the thing to try next. */
function PreviewFailure({ preview, onUseGoogleNews }: { preview: Exclude<FeedPreview, { outcome: 'ok' }>; onUseGoogleNews: (url: string) => void }) {
  return (
    <Notice tone="contradicted" title={FAILURE_TITLE[preview.outcome]} role="alert">
      <p>{preview.message}</p>

      {preview.googleNewsUrl && (
        <>
          <p>
            Se puede seguir de todas formas a través de Google Noticias: llegan el titular, el medio
            y la liga, pero no el resumen.
          </p>
          <Button type="button" variant="ghost" onClick={() => onUseGoogleNews(preview.googleNewsUrl)}>
            Usar Google Noticias
          </Button>
        </>
      )}
    </Notice>
  );
}

const FAILURE_TITLE = {
  blocked: 'El sitio nos rechaza',
  not_found: 'Esa dirección no existe',
  not_a_feed: 'Ahí no hay un feed',
  unreachable: 'No se pudo leer',
} satisfies Record<Exclude<FeedPreview['outcome'], 'ok'>, string>;

const WARNING_TITLE = {
  empty: 'El feed está vacío',
  no_dates: 'Sin fechas de publicación',
  no_summaries: 'Solo titulares',
  insecure: 'Conexión sin cifrar',
} satisfies Record<FeedPreviewWarning, string>;

const WARNING_DETAIL = {
  empty: 'Se lee bien, pero ahora mismo no trae publicaciones. Puede ser un feed nuevo o estacional.',
  no_dates: 'Ninguna publicación trae fecha, así que cada una quedará archivada en el momento en que la copiemos.',
  no_summaries: 'Las publicaciones traen titular y liga, sin resumen. Normal en consultas de Google Noticias y en cables.',
  insecure: 'La liga es http, no https. Se puede leer, pero cualquiera en la ruta ve qué sigue la redacción.',
} satisfies Record<FeedPreviewWarning, string>;
